import React from "react";
import { NavLink } from "react-router-dom";
// Components
import { Flex, Icon, KeyValuePair, Paper, Spacer, Text, Title } from "components";
// Types
import { KeyValuePairProps } from "components/Common/KeyValuePair";
import { AnimalsBreedSummaryProps } from "components/MyLivestock/Animals/AnimalsBreedSummary";
// Assets
import styles from "./AnimalsSummaryCard.module.scss";
//Helpers
import { getAnimalCountFromField } from "helpers/myLivestock";
import { BreedPills } from "components/MyLivestock/Groups/BreedPills";
import { GroupType } from "hooks/useGetGroups";
import { removeNothings } from "helpers/general";
import { DeliveryAddressType, SummaryMetrics } from "generated/graphql";

export interface AnimalsSummaryCardProps {
  breedSummaryData?: AnimalsBreedSummaryProps[];
  batchBreeds?: AnimalsBreedSummaryProps[];
  isOrganic?: boolean;
  isYard?: boolean;
  keyValueData?: KeyValuePairProps[];
  onClickUrl: string;
  showIcons?: boolean;
  subtitle?: string;
  title: string;
  summaryMetrics?: SummaryMetrics[] | null | undefined;
  location?: Pick<DeliveryAddressType, "unitName" | "locationId"> | null;
  group?: GroupType;
}

export const AnimalsSummaryCard: React.FC<AnimalsSummaryCardProps> = ({
  isOrganic = false,
  isYard = false,
  keyValueData = [],
  onClickUrl,
  showIcons = false,
  subtitle,
  title,
  location,
  summaryMetrics,
  group,
}) => {
  const animalCount = getAnimalCountFromField(summaryMetrics);

  const cardSubtitle = location?.unitName
    ? `${animalCount} Animal${animalCount !== 1 ? "s" : ""}, ${location?.unitName || "\u2014"}${
        location?.locationId ? ` - ${location?.locationId}` : ""
      }`
    : "";

  const groupSummaryMetrics = removeNothings(group?.summaryMetrics ?? []);

  return (
    <Paper shadow>
      <NavLink to={onClickUrl} className={styles.animals_summary_card}>
        <Flex item container data-testid={title.toLowerCase().replace(/\s/g, "")}>
          <Flex item itemGutter container containerDirection="column" xs="fill">
            <Flex item itemGutter>
              <Spacer baselineHeight={2.5} allowDecimal />
              <Title level={3} tertiary>
                <>
                  {showIcons && (
                    <Icon
                      className={styles["animals_summary_card__title_icon"]}
                      colour={isYard ? "yellow" : "green"}
                      name={isYard ? "yard" : "field"}
                      size="medium"
                      title={isYard ? "Yard" : "Field"}
                    />
                  )}
                  {title}
                </>
              </Title>
              <Spacer baselineHeight={1} />
              {(cardSubtitle || subtitle) && (
                <>
                  <Text>
                    <strong>{cardSubtitle || subtitle}</strong>
                  </Text>
                  <Spacer baselineHeight={1.5} allowDecimal />
                </>
              )}
              {showIcons && isOrganic && (
                <Icon
                  className={styles["animals_summary_card__organic_icon"]}
                  colour="green_dark"
                  name="organic"
                  size="medium"
                  title="Organic"
                />
              )}
            </Flex>

            {group && (
              <BreedPills
                numberOfUnidentifiedAnimalsInBatch={group.numberOfUnidentifiedAnimalsInBatch}
                animalTypeName={group.animalType?.name}
                animalBreeds={group.animalBreeds}
                isBatch={group.isBatch}
                summaryMetrics={groupSummaryMetrics}
              />
            )}
          </Flex>
          {!!keyValueData.length && (
            <Flex item itemGutter container containerResetXS={12} className={styles["animals_summary_card__key_data"]}>
              <Flex item itemGutter container>
                {keyValueData.map(
                  (item, index): JSX.Element => (
                    <Flex item xs={6} key={index} className={styles["animals_summary_card__key_value"]}>
                      <KeyValuePair {...item} boxed />
                    </Flex>
                  ),
                )}
              </Flex>
              <Spacer baselineHeight={2.5} allowDecimal />
            </Flex>
          )}
        </Flex>
      </NavLink>
    </Paper>
  );
};
